import { useNavigate } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useFragment } from '../../apollo/__generated__';
import {
  GET_QUIZ_ATTEMPT,
  UPDATE_QUIZ_ATTEMPT,
} from '../../apollo/quiz-attempts.ts';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import QuestionContent from '../questions/question-content/question-content.tsx';
import {
  QuestionAnswerType,
  QuizAttemptStatus,
} from '../../apollo/__generated__/graphql.ts';
import { XMarkIcon } from '@heroicons/react/24/outline';
import QuestionsModal, {
  IQuestionData,
} from '../test-attempt/questions-modal.tsx';
import CheckAnswers from '../test-attempt/check-answers.tsx';
import { QUIZ_FRAGMENT } from '../../apollo/quizzes.ts';
import AttemptFinish from '../quiz-attempt/attempt-finish.tsx';
import { GET_MY_QUIZ_ATTEMPTS } from '../../apollo/user-assignments.ts';
import { QuizAttemptHeader } from './quiz-attempt-header.tsx';
import { QuizAttemptFooter } from './quiz-attempt-footer.tsx';
import { QuizAttemptContext } from './quiz-attempt-provider/quiz-attempt-context.ts';
import { isEqual } from 'lodash';
import { useHighlight } from '../../contexts/highlight-provider/useHighlight.ts';
import { useQuestionHighlightVisibility } from '../../contexts/highlight-provider/use-question-highlight-visibility.ts';

type IProps = {
  id: number;
};

const QuizAttempt = ({ id }: IProps) => {
  const { highlightStickersToggle } = useHighlight();

  const navigate = useNavigate();
  const { setAttemptId, attemptQuestions, setAttemptQuestions } =
    useContext(QuizAttemptContext);
  const [update, { loading }] = useMutation(UPDATE_QUIZ_ATTEMPT, {
    refetchQueries: [GET_MY_QUIZ_ATTEMPTS],
  });
  const { data } = useSuspenseQuery(GET_QUIZ_ATTEMPT, {
    variables: { input: { id } },
    errorPolicy: 'all',
  });
  const attempt = data?.quizAttempt?.getAttempt;

  const quiz = useFragment(QUIZ_FRAGMENT, data?.quizAttempt?.getAttempt?.quiz);
  const [questionIndex, setQuestionIndex] = useState(() => {
    const index = attempt?.questions.findIndex(
      (item) => item.text || item.choice,
    );
    return index ? index + 1 : 1;
  });
  const questions = quiz?.quizQuestions;
  const question = quiz?.quizQuestions![questionIndex].question;
  const getCompleted = useCallback(
    (questionId?: number) =>
      attemptQuestions.find((item) => item.questionId === questionId) ??
      attempt?.questions.find((item) => item.questionId === questionId),
    [attempt?.questions, attemptQuestions],
  );

  const questionsData: IQuestionData[] =
    questions?.map(({ question }) => {
      const completed = getCompleted(question?.id);
      return {
        id: question!.id,
        isMarked: !!completed?.markedForReview,
        isCompleted: !!completed?.text || !!completed?.choice?.id,
        isCurrent: question?.id === quiz?.quizQuestions?.[questionIndex]?.question?.id,
      };
    }) || [];

  useEffect(() => {
    setAttemptId(id);
  }, [id, setAttemptId]);

  const getUserAnswer = (questionId?: number | null) => {
    if (!questionId) return undefined;
    const completed = getCompleted(questionId);
    if (!completed) return undefined;
    return completed?.text || completed?.choice?.id;
  };
  const [answer, setAnswer] = useState<string | number | undefined>(
    getUserAnswer(question?.id),
  );
  const [marked, setMarked] = useState<boolean>();
  const [excludedChoices, setExcludedChoices] = useState<
    number[] | null | undefined
  >();
  const [isQuestionsModal, setQuestionsModal] = useState(false);
  const [isReviewPage, setIsReviewPage] = useState(false);
  const [isCalc, setIsCalc] = useState(false);
  const questionsPanelRef = useRef<HTMLDivElement>(null);

  const jumpToQuestion = (id: number) => {
    const index = questionsData.findIndex((data) => data.id === id);
    if (index !== -1) {
      setQuestionIndex(index);
      const questionId = quiz?.quizQuestions![index].question?.id;
      setAnswer(getUserAnswer(questionId));
    }
    setIsReviewPage(false);
    setQuestionsModal(() => false);
  };
  const exit = () => navigate('/');

  useEffect(() => {
    const completed = getCompleted(question?.id);
    setExcludedChoices(completed?.excludedChoices);
    setMarked(completed?.markedForReview);
  }, [getCompleted, question?.id]);

  useEffect(() => {
    const updatedQuestions = attemptQuestions.filter((item) => {
      const completed = attempt?.questions.find(
        (q) => q.questionId === item.questionId,
      );
      if (completed) {
        return !(
          completed.markedForReview === item.markedForReview ||
          isEqual(completed.excludedChoices, item.excludedChoices) ||
          completed.text === item.text ||
          completed.choice?.id === item.choice?.id
        );
      }
      return !completed;
    });
    if (!isEqual(updatedQuestions, attemptQuestions)) {
      setAttemptQuestions(updatedQuestions);
    }
  }, [attempt?.questions, attemptQuestions, setAttemptQuestions]);

  const sendAnswer = (completed = false) => {
    if (!question?.id || !attempt?.id) return;
    const isChoiceType =
      question?.answerType === QuestionAnswerType.MultipleChoice;

    const choiceId = isChoiceType ? Number(answer) : undefined;
    const text = !isChoiceType && answer ? String(answer) : null;
    const updatedQuestions = attemptQuestions.filter(
      (item) => item.questionId !== question.id,
    );
    updatedQuestions.push({
      questionId: question.id,
      markedForReview: marked,
      excludedChoices,
      text,
      choice: choiceId ? { id: choiceId } : null,
    });
    setAttemptQuestions(updatedQuestions);

    update({
      variables: {
        input: {
          completed,
          id: attempt.id,
          question: {
            questionId: question.id,
            ...(answer && { choiceId, text }),
            excludedChoices,
            markedForReview: marked,
            ...(marked && { markedForReviewAt: new Date() }),
          },
        },
      },
    });
  };

  const setNextQuestion = () => {
    sendAnswer();

    if (questionIndex < questionsData.length - 1) {
      setQuestionIndex((prevState) => {
        const index = prevState + 1;

        setAnswer(
          questions ? getUserAnswer(questions[index].question?.id) : undefined,
        );
        return index;
      });
    }
  };

  const setPrevQuestion = () => {
    sendAnswer();

    setQuestionIndex((prevState) => {
      const index = prevState - 1;
      setAnswer(
        questions ? getUserAnswer(questions[index].question?.id) : undefined,
      );
      return index;
    });
  };

  const goToLastQuestion = () => {
    const lastQuestionIndex = (questions?.length ?? 1) - 1;
    const lastQuestionId = questions?.[lastQuestionIndex]?.question?.id;

    if (lastQuestionIndex < 0 || !lastQuestionId) {
      return;
    }

    setQuestionIndex(lastQuestionIndex);
    setAnswer(getUserAnswer(lastQuestionId));
  };

  const submitQuiz = () => {
    sendAnswer(true);
  };

  const goToReviewPage = () => {
    sendAnswer();
    setIsReviewPage(true);
    setQuestionsModal(false);
  };

  const openQuestionsModal = () => {
    if (isQuestionsModal) {
      setQuestionsModal(false);
    } else {
      sendAnswer();
      setQuestionsModal(true);
    }
  };

  useEffect(() => {
    if (!isQuestionsModal) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (questionsPanelRef.current?.contains(e.target as Node)) return;
      if ((e.target as HTMLElement).closest?.('[data-questions-toggle]')) return;
      setQuestionsModal(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setQuestionsModal(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isQuestionsModal]);

  const isShowHighLights =
    attempt?.status === QuizAttemptStatus.Archived ||
    attempt?.status === QuizAttemptStatus.InProgress;
  const { hasQuestionStickers, toggleQuestionNotesVisible } =
    useQuestionHighlightVisibility(question?.id);

  return attempt?.status === QuizAttemptStatus.Completed ||
    attempt?.status === QuizAttemptStatus.Skipped ? (
    <AttemptFinish attemptId={attempt.id} />
  ) : (
    <div>
      <div
        className={
          'attempt-viewport-height fixed inset-0 grid min-h-0 w-full grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden bg-white'
        }
      >
        <QuizAttemptHeader
          questions={questions}
          questionIndex={questionIndex}
          openQuestionsModal={openQuestionsModal}
          name={quiz?.name}
          onExit={exit}
          loading={loading}
          questionId={question?.id}
          isMath={question!.section?.name === 'math'}
          isCalc={isCalc}
          onToggleCalculator={() => setIsCalc((prevState) => !prevState)}
        />

        {isReviewPage ? (
          <CheckAnswers
            jumpToQuestion={jumpToQuestion}
            questions={questionsData}
          />
        ) : (
          <QuestionContent
            highlightStickersToggle={highlightStickersToggle}
            hasQuestionStickers={hasQuestionStickers}
            onToggleNoteVisibility={toggleQuestionNotesVisible}
            isShowHighLights={isShowHighLights}
            questionId={question?.id}
            prompt={question?.prompt || ''}
            content={question?.content}
            setMark={setMarked}
            isMark={marked}
            setExcludedChoices={setExcludedChoices}
            choicesData={
              question?.answerType === QuestionAnswerType.MultipleChoice
                ? question!.choices
                : undefined
            }
            excludedChoices={excludedChoices}
            setAnswer={setAnswer}
            answer={answer}
            isMath={question!.section?.name === 'math'}
            questionNumber={questionIndex + 1}
            isModalMode={false}
            stickyBottomActions
            isCalc={isCalc}
            setIsCalc={setIsCalc}
          />
        )}

        <QuizAttemptFooter
          questions={questions}
          questionIndex={questionIndex}
          isReviewPage={isReviewPage}
          isQuestionsModalOpen={isQuestionsModal}
          openQuestionsModal={openQuestionsModal}
          setNextQuestion={setNextQuestion}
          setPrevQuestion={() => {
            if (isReviewPage) {
              goToLastQuestion();
              setIsReviewPage(false);
              return;
            }

            setPrevQuestion();
          }}
          submit={() => {
            if (isReviewPage) {
              submitQuiz();
              return;
            }

            goToReviewPage();
          }}
        />
      </div>

      {isQuestionsModal && (
        <div
          ref={questionsPanelRef}
          className="fixed bottom-12 left-1/2 z-50 w-fit -translate-x-1/2 overflow-visible rounded-[28px] bg-white px-8 pb-6 pt-6 shadow-[0_10px_30px_rgba(15,23,42,0.18)]"
        >
          <div className="my-1 flex items-center justify-between px-1 font-semibold">
            <span>{quiz?.name || ''}</span>
            <button onClick={() => setQuestionsModal(false)} className="ml-4">
              <XMarkIcon className="h-8 w-8 rounded p-1 text-black hover:bg-light-blue" />
            </button>
          </div>
          <hr className="opacity-10" />
          <div className="pt-1">
            <QuestionsModal
              questions={questionsData}
              jumpToQuestion={jumpToQuestion}
              onGoToReviewPage={goToReviewPage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizAttempt;
